<template>
  <div v-if="draft" class="pp-sections" :style="{ '--pp-color': companyColor }">
    <h3 class="pp-panel-title">O que aparece no seu perfil</h3>
    <p class="pp-panel-subtitle">
      Tudo vem do que você já preencheu.{{ reorderable ? " Arraste para mudar a ordem." : "" }}
    </p>

    <!-- Cabecalho do perfil: sempre visivel, com os textos opcionais do topo -->
    <div class="pp-sections__row pp-sections__row--fixed" :class="{ 'pp-sections__row--open': headerOpen }">
      <span class="pp-sections__handle pp-sections__handle--hidden"><menu-icon size="16" /></span>
      <span class="pp-sections__icon"><user-icon size="18" /></span>
      <button type="button" class="pp-sections__text" @click="toggleHeader">
        <strong>Foto, nome e cargo</strong>
        <small>Título e frase de destaque</small>
      </button>
      <button
        type="button"
        class="pp-sections__chevron"
        :aria-expanded="headerOpen ? 'true' : 'false'"
        aria-label="Mostrar título e frase de destaque"
        @click="toggleHeader"
      >
        <chevron-down-icon size="18" />
      </button>
    </div>
    <div v-if="headerOpen" class="pp-sections__header-fields">
      <label class="pp-label" for="pp-headline">Título</label>
      <input
        id="pp-headline"
        class="pp-input"
        type="text"
        maxlength="80"
        :placeholder="headlinePlaceholder"
        :value="draft.headline"
        @input="edit({ headline: $event.target.value }, 'sections_header')"
      />
      <p class="pp-sections__hint">Se ficar vazio, usamos o cargo da sua experiência atual.</p>

      <label class="pp-label mt-4" for="pp-tagline">Frase de destaque</label>
      <input
        id="pp-tagline"
        class="pp-input"
        type="text"
        maxlength="140"
        placeholder="Ex.: Gosto de juntar dado e criatividade."
        :value="draft.tagline"
        @input="edit({ tagline: $event.target.value }, 'sections_header')"
      />
      <p class="pp-sections__hint">Opcional. Uma frase curta que aparece no topo.</p>
    </div>

    <draggable
      v-model="sections"
      :disabled="!reorderable"
      handle=".pp-sections__handle"
      :animation="150"
      ghost-class="pp-sections__ghost"
      tag="div"
    >
      <div v-for="section in sections" :key="section.id" class="pp-sections__item">
        <div
          class="pp-sections__row"
          :class="{
            'pp-sections__row--open': expanded === section.id,
            'pp-sections__row--off': !section.visible,
          }"
        >
          <span
            class="pp-sections__handle"
            :class="{ 'pp-sections__handle--hidden': !reorderable }"
            title="Arraste para mudar a ordem"
          >
            <menu-icon size="16" />
          </span>
          <span class="pp-sections__icon">
            <component :is="iconOf(section.id)" size="18" />
          </span>
          <button
            type="button"
            class="pp-sections__text"
            @click="onRowClick(section)"
          >
            <strong>{{ labelOf(section.id) }}</strong>
            <small>{{ subtitleOf(section.id) }}</small>
          </button>
          <button
            v-if="hasItems(section.id)"
            type="button"
            class="pp-sections__chevron"
            :aria-expanded="expanded === section.id ? 'true' : 'false'"
            :aria-label="`Escolher o que aparece em ${labelOf(section.id)}`"
            @click="toggleExpanded(section.id)"
          >
            <chevron-down-icon size="18" />
          </button>
          <button
            v-if="isLocked(section)"
            type="button"
            class="pp-sections__lock"
            :aria-label="`${labelOf(section.id)} é PRO`"
            @click="onProSection(section.id)"
          >
            <lock-icon size="16" />
            <pro-badge />
          </button>
          <div v-else class="pp-sections__switch">
            <pro-badge v-if="isProSection(section.id) && !isPro" />
            <vs-switch
              :color="companyColor"
              :value="section.visible"
              @input="setVisible(section.id, $event)"
            />
          </div>
        </div>

        <div v-if="expanded === section.id" class="pp-sections__items">
          <!-- Minimalista mostra só cargo e empresa; a descrição é opcional. -->
          <div
            v-if="section.id === 'experience' && draft.template === 'minimal'"
            class="pp-sections__option"
          >
            <span>Mostrar a descrição de cada experiência</span>
            <vs-switch
              :color="companyColor"
              :value="draft.show_experience_details"
              @input="edit({ show_experience_details: !!$event })"
            />
          </div>
          <label
            v-for="item in itemsOf(section.id)"
            :key="item.id"
            class="pp-sections__check"
          >
            <input
              type="checkbox"
              :checked="!isHidden(section.id, item.id)"
              @change="toggleItem(section.id, item.id, $event.target.checked)"
            />
            <span>
              {{ item.title }}
              <small v-if="item.period">{{ item.period }}</small>
            </span>
          </label>
        </div>
      </div>
    </draggable>

    <p v-if="!reorderable" class="pp-sections__order-note">
      <info-icon size="14" />
      <span>
        No modelo {{ templateName }}, cada bloco tem um lugar fixo. Para escolher a ordem, use o
        Clássico ou o Minimalista.
      </span>
    </p>

    <div class="pp-sections__privacy">
      <lock-icon size="18" />
      <p>
        CPF, data de nascimento, telefone, e-mail e endereço completo nunca aparecem no
        perfil público.
      </p>
    </div>

    <div class="pp-sections__google">
      <div>
        <strong>Aparecer em buscas do Google</strong>
        <small>
          {{
            draft.search_indexing
              ? "Ligado: seu perfil pode aparecer nas buscas pelo seu nome."
              : "Desligado: só quem tem o link encontra seu perfil."
          }}
        </small>
      </div>
      <vs-switch
        :color="companyColor"
        :value="draft.search_indexing"
        @input="edit({ search_indexing: !!$event }, 'sections_panel')"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import draggable from "vuedraggable";
import {
  MenuIcon,
  UserIcon,
  ChevronDownIcon,
  LockIcon,
  FileTextIcon,
  BriefcaseIcon,
  BookOpenIcon,
  GlobeIcon,
  ToolIcon,
  AwardIcon,
  VideoIcon,
  MicIcon,
  HomeIcon,
  LinkIcon,
  MailIcon,
  InfoIcon,
} from "vue-feather-icons";
import ProBadge from "./ProBadge.vue";
import mixin from "./mixin";
import { SECTIONS, templateById, plural } from "./constants";

// id da secao -> chave em items/hidden_items
const ITEM_KEYS = { experience: "experiences", education: "education" };

export default {
  name: "SectionsPanel",
  components: {
    draggable,
    ProBadge,
    MenuIcon,
    UserIcon,
    ChevronDownIcon,
    LockIcon,
    FileTextIcon,
    BriefcaseIcon,
    BookOpenIcon,
    GlobeIcon,
    ToolIcon,
    AwardIcon,
    VideoIcon,
    MicIcon,
    HomeIcon,
    LinkIcon,
    MailIcon,
    InfoIcon,
  },
  mixins: [mixin],
  data: () => ({ expanded: null, headerOpen: false }),
  computed: {
    ...mapState("publicProfile", ["draft"]),
    ...mapState("experience", ["experiencies"]),
    ...mapGetters("publicProfile", ["items", "counts", "isPro", "proSections"]),
    sections: {
      get() {
        return this.draft.sections;
      },
      set(list) {
        this.edit({ sections: list.map((s) => ({ ...s })) }, "drag_and_drop");
      },
    },
    template() {
      return templateById(this.draft.template);
    },
    reorderable() {
      return this.template.reorderable !== false;
    },
    templateName() {
      return this.template.name;
    },
    headlinePlaceholder() {
      const list = Array.isArray(this.experiencies) ? this.experiencies : [];
      const current = list.find((e) => e && e.is_current && e.business_role);
      const any = list.find((e) => e && e.business_role);
      const role = (current || any || {}).business_role;
      return role || "Ex.: Analista de Marketing";
    },
  },
  methods: {
    edit(patch, origin = "sections_panel") {
      this.$store.dispatch("publicProfile/edit", { ...patch, _track: { origin } });
    },
    toggleHeader() {
      this.headerOpen = !this.headerOpen;
      this.ppTrack("public_profile_header_panel_toggled", { open: this.headerOpen });
    },
    onProSection(id) {
      this.ppTrack("public_profile_pro_cta_clicked", { cta: `section_${id}`, section: id });
      this.$emit("paywall", `section_${id}`);
    },
    iconOf(id) {
      const icon = (SECTIONS[id] && SECTIONS[id].icon) || "FileTextIcon";
      return icon.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    },
    labelOf(id) {
      return (SECTIONS[id] && SECTIONS[id].label) || id;
    },
    isProSection(id) {
      return this.proSections.includes(id);
    },
    // Quem e gratis liga as secoes PRO para ver na previa (modo demonstracao);
    // o que e PRO nao e salvo nem publicado ate assinar.
    isLocked() {
      return false;
    },
    hasItems(id) {
      return !!ITEM_KEYS[id] && this.itemsOf(id).length > 0;
    },
    itemsOf(id) {
      const key = ITEM_KEYS[id];
      return (key && this.items[key]) || [];
    },
    isHidden(sectionId, itemId) {
      const key = ITEM_KEYS[sectionId];
      return ((this.draft.hidden_items && this.draft.hidden_items[key]) || []).includes(itemId);
    },
    visibleCount(sectionId) {
      return this.itemsOf(sectionId).filter((item) => !this.isHidden(sectionId, item.id)).length;
    },
    subtitleOf(id) {
      const c = this.counts || {};
      const total = (key) => {
        const fromItems = this.itemsOf(key).length;
        return fromItems || Number(c[ITEM_KEYS[key] || key] || 0);
      };
      switch (id) {
        case "about":
          return "Carta de apresentação";
        case "experience": {
          const n = total("experience");
          if (!n) return "Nada preenchido ainda";
          return `${this.visibleCount("experience")} de ${n} visíveis`;
        }
        case "education": {
          const n = total("education");
          if (!n) return "Nada preenchido ainda";
          const visible = this.visibleCount("education");
          return visible === n ? plural(n, "formação", "formações") : `${visible} de ${n} visíveis`;
        }
        case "languages":
          return c.languages ? plural(c.languages, "idioma", "idiomas") : "Nada preenchido ainda";
        case "skills":
          return c.skills ? plural(c.skills, "habilidade", "habilidades") : "Nada preenchido ainda";
        case "behavioral":
          // null = a API nao consultou o Recrutest; so false diz que nao tem.
          return c.has_behavioral === false
            ? "Faça o Recrutest para aparecer aqui"
            : "Recrutest DISC";
        case "video":
          return c.has_video ? "Seu vídeo de apresentação" : "Você ainda não gravou";
        case "audio":
          return c.has_audio ? "Seu áudio de apresentação" : "Você ainda não gravou";
        case "availability":
          return "Remoto, CLT ou PJ, viagens";
        case "links":
          return c.links ? plural(c.links, "link", "links") : "LinkedIn, portfólio e site";
        case "contact":
          return "Mensagem chega no seu e-mail";
        default:
          return "";
      }
    },
    onRowClick(section) {
      if (this.isLocked(section)) {
        this.$emit("paywall", `section_${section.id}`);
        return;
      }
      if (this.hasItems(section.id)) this.toggleExpanded(section.id);
    },
    toggleExpanded(id) {
      this.expanded = this.expanded === id ? null : id;
      this.ppTrack("public_profile_section_expanded", {
        section: id,
        open: this.expanded === id,
        items_count: this.itemsOf(id).length,
        visible_items_count: this.visibleCount(id),
      });
    },
    setVisible(id, value) {
      this.edit(
        { sections: this.draft.sections.map((s) => (s.id === id ? { ...s, visible: !!value } : { ...s })) },
        "section_switch",
      );
    },
    toggleItem(sectionId, itemId, visible) {
      const key = ITEM_KEYS[sectionId];
      const current = (this.draft.hidden_items && this.draft.hidden_items[key]) || [];
      const next = visible ? current.filter((id) => id !== itemId) : [...current, itemId];
      this.edit({ hidden_items: { ...this.draft.hidden_items, [key]: next } }, "section_items");
    },
  },
};
</script>

<style lang="scss">
.pp-sections {
  font-family: Roboto, sans-serif;

  > .pp-panel-subtitle {
    margin-bottom: 16px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 56px;
    padding: 6px 8px;
    border-radius: 8px;
    transition: background 0.2s ease;

    &--open {
      background: #f2f4f7;
    }

    &--off {
      .pp-sections__text strong,
      .pp-sections__icon {
        opacity: 0.6;
      }
    }
  }

  &__handle {
    display: flex;
    color: #a9a9a9;
    cursor: grab;

    &--hidden {
      visibility: hidden;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #e7edf0;
    color: var(--pp-color);
  }

  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    padding: 0;
    border: none;
    background: none;
    font-family: Roboto, sans-serif;
    text-align: left;
    cursor: pointer;

    strong {
      color: #333;
      font-size: 16px;
      font-weight: 400;
    }

    small {
      color: #696969;
      font-size: 13px;
    }
  }

  &__chevron {
    display: flex;
    padding: 4px;
    border: none;
    border-radius: 6px;
    background: none;
    color: #4f4f4f;
    cursor: pointer;
    transition: transform 0.2s ease;

    &[aria-expanded="true"] {
      transform: rotate(180deg);
    }
  }

  &__switch {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__lock {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    border: none;
    background: none;
    color: #6554c0;
    cursor: pointer;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 8px 12px 84px;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ecebeb;
    color: #333;
    font-size: 14px;
  }

  &__check {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: #4f4f4f;
    font-size: 14px;
    cursor: pointer;

    input {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-top: 1px;
      accent-color: var(--pp-color);
    }

    small {
      display: block;
      color: #696969;
      font-size: 12px;
    }
  }

  &__header-fields {
    padding: 12px 8px 16px 50px;
  }

  &__hint {
    margin-top: 4px;
    color: #696969;
    font-size: 12px;
  }

  &__order-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 8px 8px 0;
    color: #696969;
    font-size: 13px;
    line-height: 1.4;

    svg {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  &__ghost {
    opacity: 0.4;
  }

  &__privacy {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 16px;
    padding: 16px;
    border-radius: 8px;
    background: #ecebeb;
    color: #4f4f4f;
    font-size: 14px;
    line-height: 1.4;

    svg {
      flex-shrink: 0;
      color: #696969;
    }
  }

  &__google {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 20px;
    padding: 0 8px;

    strong {
      display: block;
      color: #333;
      font-size: 16px;
      font-weight: 400;
    }

    small {
      color: #696969;
      font-size: 13px;
    }
  }
}
</style>
